// ---------------------------------------------------------------------------
//  Library imports
// ---------------------------------------------------------------------------
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';

// Material-UI – components & icons
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Container,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import LogoutIcon from '@mui/icons-material/Logout';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

// Material-UI colour palette
import { blue, green, orange, pink, purple, teal } from '@mui/material/colors';

// Local modules
import LoginPage from './LoginPage';
import PrivacyPolicy from './PrivacyPolicy';

// ---------------------------------------------------------------------------
//  Axios: single pre-configured instance
// ---------------------------------------------------------------------------
const api = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001/api',
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  },
});

// ---------------------------------------------------------------------------
//  Helper utilities
// ---------------------------------------------------------------------------
const copyToClipboard = (text) => navigator.clipboard.writeText(text);

// Palette helpers (basic cycling so every brand gets a colour)
const BASE_PALETTE = [pink[100], green[100], blue[100], purple[100], orange[100], teal[100]];
const TEXT_PALETTE = [pink[800], green[800], blue[800], purple[800], orange[800], teal[800]];

const hashString = (value = '') => {
  let hash = 0;
  const str = String(value);
  for (let i = 0; i < str.length; i += 1) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return Math.abs(hash);
};

const decorateOffers = (rawOffers = []) =>
  rawOffers.map((offer, idx) => {
    const seed = `${offer.brand || ''}|${offer.code || ''}|${offer.amount || ''}`;
    const paletteIndex = BASE_PALETTE.length ? hashString(seed) % BASE_PALETTE.length : idx % BASE_PALETTE.length;
    return {
      ...offer,
      color: offer.color || BASE_PALETTE[paletteIndex],
      textColor: offer.textColor || TEXT_PALETTE[paletteIndex],
    };
  });

// ---------------------------------------------------------------------------
//  Child components (defined outside App to avoid remounts on re-render)
// ---------------------------------------------------------------------------
function OAuthRedirect({ handleOAuthCallback, fetchOffers, timePeriod }) {
  const navigate = useNavigate();

  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('code');
    if (code) {
      handleOAuthCallback(code)
        .then(() => {
          navigate('/dashboard');
          setTimeout(() => fetchOffers(timePeriod), 100);
        })
        .catch((err) => {
          console.error('OAuth callback failed:', err);
          navigate('/');
        });
    }
  }, [navigate, handleOAuthCallback, fetchOffers, timePeriod]);

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
      <Typography>Processing authentication...</Typography>
    </Box>
  );
}

function MainAppContent({
  onLogout,
  processingSession,
  loading,
  offers,
  filteredOffers,
  error,
  searchQuery,
  timePeriod,
  onChangeTimePeriod,
  onSearch,
  progress,
}) {
  const navigate = useNavigate();
  const handleLogout = () => {
    onLogout();
    navigate('/');
  };
  const isProcessing = Boolean(processingSession || loading);
  const hasDeterministicProgress = progress && progress.total > 0;
  const percent = hasDeterministicProgress
    ? Math.min(100, Math.round((progress.processed / progress.total) * 100))
    : progress?.percentage ?? 0;
  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(to bottom right, #ffe4e6, #faf5ff, #f0f9ff)' }}>
      {/* ------------------------------------------------------------------ */}
      {/* Header                                                            */}
      {/* ------------------------------------------------------------------ */}
      <Box
        sx={{
          bgcolor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid',
          borderColor: 'pink.100',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar src="/images/promopeak-logo.png" alt="PromoPeek Logo" sx={{ width: 40, height: 40, borderRadius: 1 }} />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 'bold',
                  background: 'linear-gradient(90deg, #ec4899, #a855f7)',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                }}
              >
                PromoPeek
              </Typography>
            </Box>
            <IconButton onClick={handleLogout} sx={{ color: 'grey.600', '&:hover': { bgcolor: 'pink.50' } }}>
              <LogoutIcon />
            </IconButton>
          </Box>
        </Container>
      </Box>

      {/* ------------------------------------------------------------------ */}
      {/* Main content                                                      */}
      {/* ------------------------------------------------------------------ */}
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {isProcessing && progress && (progress.total > 0 || progress.percentage >= 0) && (
          <Box
            sx={{
              mb: 3,
              p: 2,
              borderRadius: 3,
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              backdropFilter: 'blur(8px)',
              border: '1px solid',
              borderColor: 'pink.100',
              boxShadow: '0 2px 8px rgba(236,72,153,0.1)',
            }}
            aria-label="Email processing progress"
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ color: 'grey.700', fontWeight: 500 }}>
                Processed {progress.processed ?? 0} of {progress.total ?? '—'} emails
              </Typography>
              <Typography variant="body2" sx={{ color: 'pink.600', fontWeight: 'bold' }}>
                {Math.max(0, Math.min(100, percent))}%
              </Typography>
            </Box>
            <LinearProgress
              variant={hasDeterministicProgress ? 'determinate' : 'indeterminate'}
              value={hasDeterministicProgress ? Math.max(0, Math.min(100, percent)) : undefined}
              sx={{
                height: 8,
                borderRadius: 9999,
                '& .MuiLinearProgress-bar': {
                  borderRadius: 9999,
                  background: 'linear-gradient(90deg, #ec4899, #a855f7)',
                },
                backgroundColor: 'pink.50',
              }}
            />
          </Box>
        )}
        {/* Welcome message */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'grey.800', mb: 1 }}>
            {processingSession ? 'Processing your emails 🔍' : loading ? 'Looking for promos 🔍' : 'Your deals are ready! 🎉'}
          </Typography>
          <Typography variant="body1" color="grey.600">
            {processingSession ? (
              <>
                Found{' '}
                <Box component="span" sx={{ fontWeight: 'semibold', color: 'pink.600' }}>
                  {offers.length} offers
                </Box>{' '}
                so far, still processing...
              </>
            ) : loading ? (
              'Scanning your emails for available promos...'
            ) : (
              <>
                I found{' '}
                <Box component="span" sx={{ fontWeight: 'semibold', color: 'pink.600' }}>
                  {filteredOffers.length} active deals
                </Box>{' '}
                in your promotional emails
              </>
            )}
          </Typography>
        </Box>

        {/* Filters (time range & search) */}
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 4 }}>
          {/* time selector */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" sx={{ color: 'grey.700', whiteSpace: 'nowrap' }}>
              Check emails from:
            </Typography>
            <FormControl size="small">
              <Select
                value={timePeriod}
                onChange={(e) => onChangeTimePeriod(e.target.value)}
                sx={{
                  borderRadius: '9999px',
                  borderColor: 'pink.200',
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  '&:focus': { borderColor: 'pink.300' },
                  backdropFilter: 'blur(8px)',
                }}
              >
                <MenuItem value={1}>Last 24 hours</MenuItem>
                <MenuItem value={7} disabled>
                  Last Week
                </MenuItem>
                <MenuItem value={30} disabled>
                  Last Month
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* search box */}
          <TextField
            placeholder="Search for deals, brands, or codes... 🔍"
            value={searchQuery}
            onChange={(e) => onSearch(e.target.value)}
            size="small"
            fullWidth
            sx={{
              maxWidth: { sm: 400 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '9999px',
                borderColor: 'pink.200',
                bgcolor: 'rgba(255, 255, 255, 0.8)',
                '&:focus': { borderColor: 'pink.300' },
                backdropFilter: 'blur(8px)',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'grey.400' }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>

        {/* Offers grid */}
        <Box sx={{ mb: 6 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'grey.800', mb: 3 }}>
            Active Deals ({filteredOffers.length})
          </Typography>

          {((loading || processingSession) && filteredOffers.length === 0) ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 4, gap: 2 }}>
              <Box component="img" src="/images/promopeek_running_gif.gif" alt="Loading" sx={{ width: 96, height: 96 }} />
              <Typography variant="body1" color="text.secondary">
                Finding your deals...
              </Typography>
            </Box>
          ) : (!loading && !processingSession && filteredOffers.length === 0) ? (
            <Typography variant="h6" color="text.secondary" sx={{ mt: 4, textAlign: 'center' }}>
              {searchQuery ? 'No matching offers found' : 'No active offers found'}
            </Typography>
          ) : (
            <Grid container spacing={3}>
              {filteredOffers.map((offer, idx) => (
                <Grid item xs={12} sm={6} md={4} key={idx}>
                  <Card
                    sx={{
                      bgcolor: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(8px)',
                      border: '1px solid',
                      borderColor: offer.color,
                      borderRadius: 4,
                      transition: 'all 0.3s',
                      '&:hover': { transform: 'scale(1.02)', boxShadow: 3 },
                      ...(offer.urgent && {
                        border: '2px solid',
                        borderColor: 'error.light',
                        boxShadow: '0 0 0 1px rgba(211,47,47,0.1)',
                      }),
                    }}
                  >
                    <CardHeader
                      sx={{ pb: 1 }}
                      avatar={
                        <Avatar sx={{ bgcolor: offer.color, color: offer.textColor }}>
                          <LocalOfferIcon />
                        </Avatar>
                      }
                      action={
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 1, maxWidth: 160 }}>
                          {String(offer.amount)
                            .split(',')
                            .map((part, i) => (
                              <Chip
                                key={i}
                                label={part.trim()}
                                size="small"
                                sx={{ bgcolor: offer.color, color: offer.textColor, fontWeight: 'bold', maxWidth: 120 }}
                              />
                            ))}
                          {offer.urgent && <Chip label="Urgent!" size="small" color="error" sx={{ fontSize: '0.75rem' }} />}
                        </Box>
                      }
                      title={<Typography variant="h6" sx={{ fontWeight: 'bold', color: 'grey.800' }}>{offer.brand}</Typography>}
                    />
                    <CardContent sx={{ pt: 0 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'grey.500' }}>
                          <AccessTimeIcon sx={{ fontSize: 16 }} />
                          <Typography
                            variant="body2"
                            sx={{ color: offer.urgent ? 'error.main' : 'inherit', fontWeight: offer.urgent ? 'medium' : 'normal' }}
                          >
                            Expires in {offer.expiry}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Box sx={{ flex: 1, bgcolor: 'grey.100', borderRadius: '9999px', py: 1, px: 2, textAlign: 'center' }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                              {offer.code}
                            </Typography>
                          </Box>
                          <IconButton size="small" onClick={() => copyToClipboard(offer.code)} sx={{ '&:hover': { bgcolor: 'pink.100' } }}>
                            <ContentCopyIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {/* Footer */}
        <Box sx={{ mt: 8, textAlign: 'center' }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1,
              px: 3,
              py: 1.5,
              bgcolor: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(8px)',
              borderRadius: '9999px',
              border: '1px solid',
              borderColor: 'pink.100',
            }}
          >
            <Box
              sx={{
                width: 24,
                height: 24,
                background: 'linear-gradient(135deg, #ec4899, #a855f7)',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  bgcolor: 'white',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box sx={{ width: 4, height: 4, bgcolor: 'pink.400', borderRadius: '50%' }} />
              </Box>
            </Box>
            <Typography variant="body2" color="grey.600">
              Happy saving! Your hamster friend is always watching for new deals 🐹✨
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Container>
    </Box>
  );
}

// ---------------------------------------------------------------------------
//  Main component
// ---------------------------------------------------------------------------
function App() {
  // ------------------------- state ----------------------------------------
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timePeriod, setTimePeriod] = useState(1); // days back
  const [searchQuery, setSearchQuery] = useState('');
  const [processingSession, setProcessingSession] = useState(null);
  const [progress, setProgress] = useState(null);

  const filteredOffers = useMemo(() => {
    if (!searchQuery.trim()) {
      return offers;
    }
    const normalized = (value) => String(value || '').toLowerCase();
    const q = searchQuery.toLowerCase();
    return offers.filter((offer) =>
      normalized(offer.brand).includes(q) ||
      normalized(offer.code).includes(q) ||
      normalized(offer.amount).includes(q) ||
      normalized(offer.expiry).includes(q)
    );
  }, [offers, searchQuery]);

  // ---------------------- OAuth helpers -----------------------------------
  const handleOAuthCallback = async (code) => {
    try {
      const res = await api.get(`/auth/callback?code=${code}`);
      if (res.data.error) throw new Error(res.data.error);
      setIsAuthenticated(true);
      return true;
    } catch (err) {
      setError(`Authentication failed: ${err.response?.data?.error || err.message}`);
      throw err;
    }
  };

  // ----------------------- auth actions -----------------------------------
  const handleLogin = async () => {
    try {
      const { data } = await api.get('/auth/url');
      if (data.error) throw new Error(data.error);
      window.location.href = data.url;
    } catch (err) {
      setError(`Login failed: ${err.response?.data?.error || err.message}`);
    }
  };

  // --------------------- data fetching ------------------------------------
  const fetchOffers = async (period = 1) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get('/offers', { params: { days: period } });
      if (data.error) throw new Error(data.error);

      // Handle different response types from new async backend
      if (data.status === 'completed' && Array.isArray(data.offers)) {
        // Cached results available immediately
        const coloured = decorateOffers(data.offers);
        setOffers(coloured);
        setProgress({
          processed: data.processed_messages ?? data.total_messages ?? coloured.length ?? 0,
          total: data.total_messages ?? coloured.length ?? 0,
          percentage: data.progress_percentage ?? 100,
          status: data.status ?? 'completed',
        });
        setLoading(false);
      } else if (data.status === 'processing' && data.session_id) {
        // Async processing started - store session and start polling
        setProcessingSession(data.session_id);
        setProgress({
          processed: data.processed_messages ?? 0,
          total: data.total_messages ?? 0,
          percentage: data.progress_percentage ?? 0,
          status: data.status,
        });
        setLoading(false);
        startProgressPolling(data.session_id);
      } else if (Array.isArray(data.offers)) {
        // Fallback: handle old API format if needed
        const coloured = decorateOffers(data.offers);
        setOffers(coloured);
        setProgress({
          processed: coloured.length,
          total: coloured.length,
          percentage: coloured.length ? 100 : 0,
          status: data.status ?? 'completed',
        });
        setLoading(false);
      } else {
        // No offers found
        setOffers([]);
        setProgress(null);
        setLoading(false);
      }
    } catch (err) {
      setError(`Failed to fetch offers: ${err.response?.data?.error || err.message}`);
      setProgress(null);
      setLoading(false);
    }
  };

  // --------------------- progress polling ---------------------------------
  const startProgressPolling = (sessionId) => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await api.get(`/offers/status/${sessionId}`);
        const data = response.data;

        // Update progress state
        setProgress({
          percentage: data.progress_percentage || 0,
          processed: data.processed_messages || 0,
          total: data.total_messages || 0,
          status: data.status
        });

        // Update offers as they come in
        if (Array.isArray(data.offers)) {
          setOffers(decorateOffers(data.offers));
        }

        // Stop polling when complete
        if (data.status === 'completed' || data.status === 'failed' ||
            (data.processed_messages >= data.total_messages && data.total_messages > 0)) {
          clearInterval(pollInterval);
          setProcessingSession(null);
          setProgress(null);
          setLoading(false); // Ensure loading is false when complete
          if (data.status === 'failed') {
            setError('Processing failed. Please try again.');
          }
        }
      } catch (error) {
        console.error('Failed to get progress:', error);
        // Don't stop polling on network errors, just log them
      }
    }, 2000); // Poll every 2 seconds

    // Store interval ID for cleanup
    setProcessingSession(prev => ({ id: sessionId, intervalId: pollInterval }));

    // Set loading to false only after progress polling is set up
    setLoading(false);
  };

  // Cleanup polling on unmount or logout
  useEffect(() => {
    return () => {
      if (processingSession && processingSession.intervalId) {
        clearInterval(processingSession.intervalId);
      }
    };
  }, [processingSession]);

  // ----------------------- UI helpers -------------------------------------
  const handleSearch = (query) => {
    setSearchQuery(query);
  };

  // -------------------------- render --------------------------------------
  return (
    <Router>
      <Routes>
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route
          path="/oauth2callback"
          element={<OAuthRedirect handleOAuthCallback={handleOAuthCallback} fetchOffers={fetchOffers} timePeriod={timePeriod} />}
        />
        <Route
          path="/dashboard"
          element={
            isAuthenticated ? (
              <MainAppContent
                onLogout={() => {
                  // Clear any active polling
                  if (processingSession && processingSession.intervalId) {
                    clearInterval(processingSession.intervalId);
                  }
                  setIsAuthenticated(false);
                  setOffers([]);
                  setSearchQuery('');
                  setProcessingSession(null);
                  setProgress(null);
                }}
                processingSession={processingSession}
                loading={loading}
                offers={offers}
                filteredOffers={filteredOffers}
                error={error}
                searchQuery={searchQuery}
                timePeriod={timePeriod}
                onChangeTimePeriod={(v) => {
                  if (v !== timePeriod) {
                    setTimePeriod(v);
                    fetchOffers(v);
                  }
                }}
                onSearch={handleSearch}
                progress={progress}
              />
            ) : (
              <Navigate to="/" replace />
            )
          }
        />
        <Route
          path="/"
          element={!isAuthenticated ? <LoginPage onLogin={handleLogin} error={error} /> : <Navigate to="/dashboard" replace />}
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
